import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    CardContent,
    CardMedia,
    Button,
    Rating,
    Stack,
    Chip,
    IconButton,
    Paper,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { motion } from "framer-motion";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";
// import { apiList, invokeApi } from "../../services/apiServices";
// import { config } from "../../config/config";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

const ProductGrid = ({ onCartUpdate }) => {
    const [cookies] = useCookies();
    const navigate = useNavigate();
    const [productsData, setProductsData] = useState([]);
    const [cartItems, setCartItems] = useState([]);
    const [openCheckout, setOpenCheckout] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const scrollRef = useRef(null);

    // Animation variants to match Courses component
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
        hover: {
            scale: 1.03,
            boxShadow: "0 8px 25px rgba(109, 22, 117, 0.3)",
            transition: { duration: 0.3 }
        }
    };

    // Load cart from session storage
    useEffect(() => {
        const storedCart = sessionStorage.getItem("cart");
        if (storedCart) {
            setCartItems(JSON.parse(storedCart));
        }
    }, [onCartUpdate]);

    // Auto-scroll carousel
    useEffect(() => {
        const scrollInterval = setInterval(() => {
            if (scrollRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
                if (scrollLeft + clientWidth >= scrollWidth - 1) {
                    scrollRef.current.scrollTo({ left: 0, behavior: "instant" });
                } else {
                    scrollRef.current.scrollBy({ left: 10, behavior: "smooth" });
                }
            }
        }, 100);
        return () => clearInterval(scrollInterval);
    }, []);

    // Fetch product data
    const fetchProductData = async () => {
        // try {
        //   const response = await invokeApi(
        //     config.getMyCollege + apiList.getAllProducts,
        //     {}
        //   );
        //   if (response?.status === 200) {
        //     setProductsData(response.data.products);
        //   } else {
        //     console.error("Failed to get data. Please try again later!!");
        //   }
        // } catch (error) {
        //   console.error("Failed to get data. Please try again later!!");
        // }
    };

    useEffect(() => {
        fetchProductData();
    }, []);

    // Add to cart
    const handleAddToCart = (product) => {
        const existingItem = cartItems.find((item) => item.id === product.id);
        let updatedCart;
        if (existingItem) {
            updatedCart = cartItems.map((item) =>
                item.id === product.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            );
        } else {
            updatedCart = [...cartItems, { ...product, quantity: 1 }];
        }
        setCartItems(updatedCart);
        sessionStorage.setItem("cart", JSON.stringify(updatedCart));
        if (onCartUpdate) onCartUpdate(updatedCart);
    };

    // Buy now
    const handleBuyProduct = (product) => {
        const updatedCart = [{ ...product, quantity: 1 }];
        sessionStorage.setItem("cart", JSON.stringify(updatedCart));
        setCartItems(updatedCart);
        if (onCartUpdate) onCartUpdate(updatedCart);
        setOpenCheckout(true);
    };

    // Remove from cart
    const handleRemoveFromCart = (productId) => {
        const existingItem = cartItems.find((item) => item.id === productId);
        let updatedCart;
        if (existingItem.quantity > 1) {
            updatedCart = cartItems.map((item) =>
                item.id === productId
                    ? { ...item, quantity: item.quantity - 1 }
                    : item
            );
        } else {
            updatedCart = cartItems.filter((item) => item.id !== productId);
        }
        setCartItems(updatedCart);
        sessionStorage.setItem("cart", JSON.stringify(updatedCart));
        if (onCartUpdate) onCartUpdate(updatedCart);
    };

    // Calculate totals
    const calculateTotals = () => {
        const items = Array.isArray(cartItems) ? cartItems : [];
        const subtotal = items.reduce(
            (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
            0
        );
        const discount = items.reduce(
            (sum, item) =>
                sum +
                ((item.scratchPrice || 0) - (item.price || 0)) *
                (item.quantity || 0),
            0
        );
        const total = subtotal;
        const savings = discount;
        return { subtotal, discount, total, savings };
    };

    const { total, savings } = calculateTotals();

    return (
        <>
            <Box
                component={motion.div}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                sx={{
                    py: 6,
                    maxWidth: 1400,
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    textAlign: "center",
                    bgcolor: "rgba(255, 234, 223, 0.3)",
                    px: 2,
                }}
            >
                {/* Header Section */}
                <motion.div variants={itemVariants}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            fontSize: "30px",
                            textAlign: "center",
                            color: "#6d1675",
                            letterSpacing: "0.5px",
                            pb: 1,
                            mb: 0,
                        }}
                    >
                        Recently <span style={{ color: "#ffc20f" }}>Purchased Items</span>
                    </Typography>

                    {/* Flower Divider */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: 300,
                            mx: "auto",
                            mb: 4,
                        }}
                    >
                        <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                        <Box
                            sx={{
                                px: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <LocalFloristIcon sx={{ fontSize: 28, color: "#ffc20f" }} />
                        </Box>
                        <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                    </Box>
                </motion.div>

                <Box sx={{ position: "relative" }}>
                    {/* Left arrow */}
                    <IconButton
                        onClick={() =>
                            scrollRef.current?.scrollBy({ left: -300, behavior: "smooth" })
                        }
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: -20,
                            color: "white",
                            transform: "translateY(-50%)",
                            zIndex: 10,
                            backgroundColor: "#6d1675",
                            boxShadow: "0 4px 12px rgba(109, 22, 117, 0.4)",
                            "&:hover": { 
                                backgroundColor: "#ffc20f",
                                color: "#6d1675"
                            },
                        }}
                    >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>

                    {/* Right arrow */}
                    <IconButton
                        onClick={() =>
                            scrollRef.current?.scrollBy({ left: 300, behavior: "smooth" })
                        }
                        sx={{
                            position: "absolute",
                            top: "50%",
                            right: -20,
                            transform: "translateY(-50%)",
                            zIndex: 10,
                            color: "white",
                            backgroundColor: "#6d1675",
                            boxShadow: "0 4px 12px rgba(109, 22, 117, 0.4)",
                            "&:hover": { 
                                backgroundColor: "#ffc20f",
                                color: "#6d1675"
                            },
                        }}
                    >
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>

                    {/* Product list */}
                    <Box
                        ref={scrollRef}
                        sx={{
                            display: "flex",
                            gap: 3,
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollBehavior: "smooth",
                            pb: 2,
                            px: 1,
                        }}
                    >
                        {productsData.map((item, idx) => {
                            const cartItem = cartItems.find(
                                (cartItem) => cartItem.id === item.id
                            );
                            const quantity = cartItem ? cartItem.quantity : 0;

                            return (
                                <motion.div
                                    key={idx}
                                    variants={cardVariants}
                                    whileHover="hover"
                                >
                                    <Box
                                        sx={{
                                            borderRadius: "16px",
                                            p: 2,
                                            position: "relative",
                                            border: "2px solid #f0f0f0",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minWidth: 280,
                                            backgroundColor: "#fff",
                                            cursor: "pointer",
                                            boxShadow: "0 4px 15px rgba(109, 22, 117, 0.1)",
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                borderColor: "#ffc20f",
                                            },
                                        }}
                                        onClick={() => navigate(`/products/${item.ogUrl}`)}
                                    >
                                        {/* Chips */}
                                        <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 2 }}>
                                            <Chip
                                                label="New Launch"
                                                sx={{
                                                    backgroundColor: "#6d1675",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    borderRadius: "8px",
                                                    fontSize: 11,
                                                    height: 24,
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
                                            <Chip
                                                label={`${Math.round(
                                                    ((item.scratchPrice - item.price) * 100) /
                                                    item.scratchPrice
                                                )}% Off`}
                                                sx={{
                                                    backgroundColor: "#ffc20f",
                                                    color: "#6d1675",
                                                    fontWeight: "bold",
                                                    borderRadius: "8px",
                                                    fontSize: 11,
                                                    height: 24,
                                                }}
                                            />
                                        </Box>

                                        {/* Image */}
                                        <CardMedia
                                            component="img"
                                            image={item.featuredImage}
                                            alt={item.productName}
                                            sx={{
                                                objectFit: "contain",
                                                height: 220,
                                                borderRadius: "12px",
                                                backgroundColor: "#fcf6eb",
                                                p: 2,
                                                mb: 2,
                                            }}
                                        />

                                        {/* Content */}
                                        <CardContent sx={{ px: 0, py: 0 }}>
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{ 
                                                    mb: 1,
                                                    fontSize: "16px",
                                                    color: "#6d1675",
                                                    minHeight: 40,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    textAlign: "center"
                                                }}
                                            >
                                                {item.productName}
                                            </Typography>

                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1}
                                                sx={{ mb: 1, justifyContent: "center" }}
                                            >
                                                <Rating 
                                                    value={4.5} 
                                                    precision={0.5} 
                                                    readOnly 
                                                    size="small" 
                                                    sx={{
                                                        "& .MuiRating-iconFilled": {
                                                            color: "#ffc20f",
                                                        }
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
                                                    1088 reviews
                                                </Typography>
                                            </Stack>

                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ mb: 1, justifyContent: "center" }}
                                            >
                                                <Typography variant="h6" fontWeight="bold" sx={{ color: "#6d1675" }}>
                                                    ₹{item.price}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        textDecoration: "line-through",
                                                        color: "#999",
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    ₹{item.scratchPrice}
                                                </Typography>
                                            </Stack>

                                            <Typography
                                                variant="body2"
                                                sx={{ 
                                                    color: "#ffc20f", 
                                                    fontWeight: "bold",
                                                    fontSize: "12px",
                                                    mb: 2
                                                }}
                                            >
                                                Best Price ₹{item.price}{" "}
                                                <Typography component="span" sx={{ color: "#666" }}>
                                                    with coupon
                                                </Typography>
                                            </Typography>
                                        </CardContent>

                                        {/* Actions */}
                                        <Stack
                                            spacing={1.5}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ mb: 1 }}
                                        >
                                            {quantity > 0 ? (
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        borderRadius: 50,
                                                        px: 1,
                                                        borderColor: "#6d1675",
                                                        backgroundColor: "#f8f4ff",
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveFromCart(item.id);
                                                        }}
                                                        sx={{ color: "#6d1675" }}
                                                    >
                                                        <RemoveIcon fontSize="small" />
                                                    </IconButton>
                                                    <Typography sx={{ px: 1, fontWeight: "bold", color: "#6d1675" }}>
                                                        {quantity}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAddToCart(item);
                                                        }}
                                                        sx={{ color: "#6d1675" }}
                                                    >
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Paper>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ShoppingCartIcon fontSize="small" />}
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAddToCart(item);
                                                    }}
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        minWidth: 120,
                                                        backgroundColor: "#ffc20f",
                                                        color: "#6d1675",
                                                        borderRadius: 50,
                                                        textTransform: "none",
                                                        fontWeight: "bold",
                                                        fontSize: "0.75rem",
                                                        "&:hover": { 
                                                            backgroundColor: "#6d1675",
                                                            color: "#fff"
                                                        },
                                                        boxShadow: "0 2px 8px rgba(255, 194, 15, 0.3)",
                                                    }}
                                                >
                                                    Add to cart
                                                </Button>
                                            )}

                                            <Button
                                                variant="outlined"
                                                startIcon={<FlashOnIcon fontSize="small" />}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleBuyProduct(item);
                                                }}
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    minWidth: 100,
                                                    borderColor: "#6d1675",
                                                    color: "#6d1675",
                                                    borderRadius: 50,
                                                    textTransform: "none",
                                                    fontWeight: "bold",
                                                    fontSize: "0.75rem",
                                                    "&:hover": {
                                                        borderColor: "#ffc20f",
                                                        backgroundColor: "#6d1675",
                                                        color: "#fff",
                                                    },
                                                }}
                                            >
                                                Buy Now
                                            </Button>
                                        </Stack>
                                    </Box>
                                </motion.div>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Checkout Drawer */}
            {/* <CheckoutDrawer
                cartItems={cartItems}
                savings={savings}
                payAmount={total}
                openCheckout={openCheckout}
                onCloseCheckout={() => setOpenCheckout(false)}
            /> */}
        </>
    );
};

export default ProductGrid;